import { computed, ref, shallowRef, watchEffect } from 'vue';
import { defineStore } from 'pinia';
import { Theme, type ThemePreference } from '@/common/Theme';
import { config, appConfigBuilder, AppConfigLayer } from '@/config';
import type { AppConfigSchema } from '@/config/defaults';
import type { JsonStorageProvider } from '@/config/providers/JsonStorageProvider';

export const useSettingsStore = defineStore('settings', () => {
  const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const systemPrefersDark = ref(darkQuery.matches);
  darkQuery.addEventListener?.('change', (event) => {
    systemPrefersDark.value = event.matches;
  });

  const storage = appConfigBuilder.getLayer(AppConfigLayer.LOCAL_STORAGE)?.provider as
    | JsonStorageProvider<AppConfigSchema>
    | undefined;

  const theme = shallowRef(Theme.fromStored({ stored: config.theme }));

  const preference = computed(() => theme.value.preference);
  const resolvedTheme = computed(() =>
    theme.value.resolve({ systemPrefersDark: systemPrefersDark.value })
  );

  watchEffect(() => {
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${resolvedTheme.value}`);
  });

  function setTheme(chosen: ThemePreference) {
    theme.value = Theme.create({ preference: chosen });
    storage?.saveValue('theme', chosen);
  }

  function toggleTheme() {
    setTheme(theme.value.next().preference);
  }

  // Timestamps show in UTC unless the user turns on local dates.
  const localDates = ref(config.localDates === true);

  function setLocalDates(enabled: boolean) {
    localDates.value = enabled;
    storage?.saveValue('localDates', enabled);
  }

  return { preference, resolvedTheme, setTheme, toggleTheme, localDates, setLocalDates };
});
